import { Provider } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { createSelector, Store } from '@ngrx/store';
import { combineLatest, map } from 'rxjs';

import { FeatureFlagKeys } from '@ninety/platform/state/app-entities/feature-flag/feature-flag-state.model';
import { selectFeatureFlag } from '@ninety/platform/state/app-entities/feature-flag/feature-flag-state.selectors';
import { CurrentUserSelectors } from '@ninety/platform/state/app-entities/user-list/user-list-state.selectors';
import { AddTeammatesActions } from '@ninety/platform/state/app-entities/users/users-state.actions';
import {
  selectCurrentUserIsAdminOrOwner,
  selectCurrentUserIsManager,
  selectIsEmailAvailable,
} from '@ninety/platform/state/app-entities/users/users-state.selectors';
import {
  selectCompanyBillingCounts,
  selectIsFreemiumPlan,
  selectIsPaidPlan,
  selectIsTrialing,
} from '@ninety/platform/state/app-global/billing/billing-state.selectors';
import { selectCompanySettings } from '@ninety/platform/state/app-global/company/company-state.selectors';
import { SubscriptionActions } from '@ninety/platform/state/app-global/company/subscription/subscription-state.actions';
import {
  ADD_TEAMMATES_SETTLED,
  ADD_TEAMMATES_TEAMS,
  CLOSE_ADD_TEAMMATES_DIALOG,
  COMPANY_BILLING_COUNTS,
  CURRENT_USER_IS_ADMIN_OR_OWNER,
  CURRENT_USER_IS_MANAGER,
  GO_TO_BILLING_MANAGE_SEATS,
  IS_EMAIL_AVAILABLE,
  IS_FREEMIUM_PLAN,
  IS_PAID_PLAN,
  IS_TRIALING,
  IsEmailAvailable,
  OPEN_UNSAVED_CHANGES_DIALOG,
  RESTRICT_DOMAIN_SETTINGS,
  SHOW_BULK_IMPORT_BUTTON,
  SUBMIT_ADD_TEAMMATES,
  SubmitAddTeammates,
} from '@ninety/ui/core';

// Derived selectors formerly declared in the dialog component; relocated here so the component carries
// no `@ninety/platform/state` dependency. (DEV-26530 Part 8b)
const selectRestrictInviteByDomainFlag = selectFeatureFlag(FeatureFlagKeys.webRestrictInviteByDomain);
const selectRestrictDomainSettings = createSelector(
  selectCompanySettings,
  selectRestrictInviteByDomainFlag,
  (settings, hasFlag) => (hasFlag ? (settings?.organizationSettings ?? null) : null)
);

/**
 * Binds the tokens consumed by `AddTeammatesDialogComponent` / `AddTeammatesDialogStore` (now packaged
 * in `@ninety/ui/core`) to their `@ninety/platform/state` selectors and actions. `ROLE_SELECT_OPTIONS`
 * is already bound app-wide (INLINE_ROLE_SELECT_PROVIDERS) and is reused. This composition-root wiring is
 * the single place that depends on both the tokens and the platform state lib. (DEV-26530 Part 8b)
 */
export const ADD_TEAMMATES_DIALOG_PROVIDERS: Provider[] = [
  {
    provide: COMPANY_BILLING_COUNTS,
    useFactory: (store: Store) => store.select(selectCompanyBillingCounts),
    deps: [Store],
  },
  { provide: IS_PAID_PLAN, useFactory: (store: Store) => store.select(selectIsPaidPlan), deps: [Store] },
  { provide: IS_FREEMIUM_PLAN, useFactory: (store: Store) => store.select(selectIsFreemiumPlan), deps: [Store] },
  { provide: IS_TRIALING, useFactory: (store: Store) => store.select(selectIsTrialing), deps: [Store] },
  {
    provide: ADD_TEAMMATES_TEAMS,
    useFactory: (store: Store) => store.select(CurrentUserSelectors.selectTeamsForAddTeammates),
    deps: [Store],
  },
  {
    provide: CURRENT_USER_IS_ADMIN_OR_OWNER,
    useFactory: (store: Store) => store.select(selectCurrentUserIsAdminOrOwner),
    deps: [Store],
  },
  {
    provide: CURRENT_USER_IS_MANAGER,
    useFactory: (store: Store) => store.select(selectCurrentUserIsManager),
    deps: [Store],
  },
  {
    provide: SHOW_BULK_IMPORT_BUTTON,
    useFactory: (store: Store) =>
      combineLatest([
        store.select(selectCurrentUserIsAdminOrOwner),
        // Temporary: the bulk-import feature is being swapped out behind this flag.
        // eslint-disable-next-line @ngrx/avoid-combining-selectors
        store.select(selectFeatureFlag(FeatureFlagKeys.clientFacingDataUpload)),
      ]).pipe(map(([isAdminOrOwner, flag]) => isAdminOrOwner && flag)),
    deps: [Store],
  },
  {
    provide: RESTRICT_DOMAIN_SETTINGS,
    useFactory: (store: Store) => store.select(selectRestrictDomainSettings),
    deps: [Store],
  },
  {
    provide: IS_EMAIL_AVAILABLE,
    useFactory:
      (store: Store): IsEmailAvailable =>
      (email: string) =>
        store.select(selectIsEmailAvailable(email)),
    deps: [Store],
  },
  {
    provide: OPEN_UNSAVED_CHANGES_DIALOG,
    useFactory: (store: Store) => () => store.dispatch(AddTeammatesActions.openUnsavedChangesDialog()),
    deps: [Store],
  },
  {
    provide: CLOSE_ADD_TEAMMATES_DIALOG,
    useFactory: (store: Store) => () => store.dispatch(AddTeammatesActions.closeAddTeammatesDialog()),
    deps: [Store],
  },
  {
    provide: GO_TO_BILLING_MANAGE_SEATS,
    useFactory: (store: Store) => () => store.dispatch(SubscriptionActions.goToBillingManageSeats()),
    deps: [Store],
  },
  {
    provide: SUBMIT_ADD_TEAMMATES,
    useFactory:
      (store: Store): SubmitAddTeammates =>
      userForms =>
        store.dispatch(AddTeammatesActions.addTeammates({ userForms })),
    deps: [Store],
  },
  {
    provide: ADD_TEAMMATES_SETTLED,
    useFactory: (actions$: Actions) =>
      actions$.pipe(
        ofType(AddTeammatesActions.createUsersSuccess, AddTeammatesActions.createUsersFailed),
        map(() => undefined)
      ),
    deps: [Actions],
  },
];
